// Salesreport.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import "./TrialBalance.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

// ✅ Searchable dropdown component
const SearchableDropdown = ({ options, value, onChange, placeholder, label }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (selected) => {
    onChange(selected);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={wrapperRef}>
      <label style={{ color: '#2e3192', fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ced4da', borderRadius: '0.375rem', background: 'white' }}>
        <input
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: '0.5rem',
            border: 'none',
            outline: 'none',
            borderRadius: '0.375rem',
            background: 'transparent',
            minWidth: '0',
          }}
        />
        <span style={{ padding: '0 8px', color: '#6c757d', cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}>▼</span>
      </div>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          maxHeight: '200px',
          overflowY: 'auto',
          background: 'white',
          border: '1px solid #ced4da',
          borderRadius: '0.375rem',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '0.5rem', color: '#6c757d' }}>No options</div>
          ) : (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                style={{
                  padding: '0.5rem',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f3f5',
                  background: opt.value === value ? '#f0f0ff' : 'white'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0ff'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ✅ Helper: wait for all images in a print window to finish loading
const waitForImagesThenPrint = (win, doPrint) => {
  if (!win || win.closed) return;
  const doc = win.document;
  const images = Array.from(doc.images || []);
  const total = images.length;

  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    doPrint();
  };

  if (total === 0) {
    setTimeout(triggerPrint, 300);
    return;
  }

  let settled = 0;
  const onSettled = () => {
    settled++;
    if (settled >= total) setTimeout(triggerPrint, 150);
  };

  images.forEach((img) => {
    if (img.complete && img.naturalWidth > 0) onSettled();
    else {
      img.addEventListener('load', onSettled, { once: true });
      img.addEventListener('error', onSettled, { once: true });
    }
  });

  setTimeout(triggerPrint, 2000);
};

const Salesreport = () => {
  const [sales, setSales] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [batchNumbers, setBatchNumbers] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedBatchNo, setSelectedBatchNo] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');

  const printRef = useRef();
  const posPrintRef = useRef();

  const formatCurrency = useCallback((amount) => {
    const currencyCode = getCompanyCurrency(company);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [company]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      const payload = res?.data?.data || res?.data || null;
      setCompany(payload);
    } catch (err) {
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const res = await axios.get(`${url}/api/products/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      if (res.data.success && Array.isArray(res.data.data)) setProducts(res.data.data);
      else setProducts([]);
    } catch (err) {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      const res = await axios.get(`${url}/api/customer/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      if (res.data.success && Array.isArray(res.data.data)) setCustomers(res.data.data);
      else setCustomers([]);
    } catch (err) {
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (selectedProductId) params.product_id = selectedProductId;
      if (selectedCustomerId) params.customer_id = selectedCustomerId;
      params.limit = 5000;

      const res = await axios.get(`${url}/api/sales`, {
        headers: getAuthHeaders(),
        params,
      });
      const data = res.data.data || [];
      
      // ✅ Fetch expenses for the same period
      const expParams = {};
      if (fromDate) expParams.start_date = fromDate;
      if (toDate) expParams.end_date = toDate;
      const expRes = await axios.get(`${url}/api/expense`, { headers: getAuthHeaders(), params: expParams });
      const expensesData = expRes.data.data || [];
      setTotalExpenses(expensesData.reduce((sum, exp) => sum + Number(exp.amount || 0), 0));

      const invoiceIds = new Set();
      data.forEach(tx => {
        const invId = tx.sales_invoice_id?._id || tx.sales_invoice_id;
        if (invId) invoiceIds.add(invId);
      });

      const invoiceGrandTotalMap = {};
      for (const invId of invoiceIds) {
        try {
          const invRes = await axios.get(`${url}/api/sales/invoice/${invId}`, {
            headers: getAuthHeaders(),
          });
          if (invRes.data.success) {
            const invData = invRes.data.data;
            invoiceGrandTotalMap[invId] = {
              grand_total: invData.grand_total || 0,
              invoice_no: invData.invoice_no || '',
            };
          }
        } catch (e) {
          const tx = data.find(t => (t.sales_invoice_id?._id || t.sales_invoice_id) === invId);
          invoiceGrandTotalMap[invId] = {
            grand_total: tx?.grand_total || 0,
            invoice_no: tx?.invoice_no || '',
          };
        }
      }

      const enrichedTxns = data.map(tx => {
        const invId = tx.sales_invoice_id?._id || tx.sales_invoice_id;
        const invData = invoiceGrandTotalMap[invId] || {};
        return {
          ...tx,
          invoice_grand_total: invData.grand_total || tx.grand_total || 0,
          invoice_no_display: invData.invoice_no || tx.invoice_no || '',
        };
      });

      const batches = new Set();
      enrichedTxns.forEach(item => { if (item.batch_number) batches.add(item.batch_number); });
      setBatchNumbers(Array.from(batches).sort());

      let filtered = enrichedTxns;
      if (selectedBatchNo) filtered = filtered.filter(item => item.batch_number === selectedBatchNo);
      if (invoiceNo.trim()) {
        const searchLower = invoiceNo.trim().toLowerCase();
        filtered = filtered.filter(item =>
          (item.invoice_no_display || item.invoice_no || '').toLowerCase().includes(searchLower)
        );
      }

      setSales(enrichedTxns);
      setFilteredData(filtered);
      if (filtered.length === 0 && (selectedBatchNo || invoiceNo.trim() || enrichedTxns.length > 0)) {
        setError(selectedBatchNo ? `No sales records found for batch "${selectedBatchNo}".` :
                 invoiceNo.trim() ? `No sales records found for invoice "${invoiceNo.trim()}".` :
                 'No sales records found for the selected filters.');
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load sales report');
      setSales([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedProductId, selectedCustomerId, selectedBatchNo, invoiceNo]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchReport(); }, 300);
    return () => clearTimeout(timer);
  }, [fromDate, toDate, selectedProductId, selectedCustomerId, selectedBatchNo, invoiceNo, fetchReport]);

  useEffect(() => {
    fetchCompany();
    fetchProducts();
    fetchCustomers();
  }, [fetchCompany, fetchProducts, fetchCustomers]);

  useEffect(() => {
    if (!loadingCompany && !loadingProducts && !loadingCustomers) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCompany, loadingProducts, loadingCustomers]);

  const handleGenerate = () => fetchReport();

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    if (company.logo.startsWith('http')) return company.logo;
    const cleanPath = company.logo.replace(/^\/+/, '');
    return `${url}/${cleanPath}`;
  };

  // ============================================================
  // ✅ A4 LANDSCAPE REPORT PRINT — uses browser's native print
  // ============================================================
  const handlePrint = () => window.print();

  // ============================================================
  // ✅ POS RECEIPT PRINT — uses new window (thermal layout)
  // ============================================================
  const handlePosPrint = () => {
    if (!posPrintRef.current) return;
    const printContent = posPrintRef.current.innerHTML;

    const win = window.open('', '_blank');
    if (!win) {
      alert('Please allow pop-ups to print the receipt.');
      return;
    }

    win.document.write(`
      <html>
        <head>
          <title>Sales Receipt</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding: 10px; max-width: 350px; }
            .receipt-header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
            .receipt-header h1 { font-size: 16px; margin: 2px 0; }
            .receipt-header p { margin: 1px 0; font-size: 10px; }
            .receipt-header .logo { max-height: 40px; margin-bottom: 4px; }
            .receipt-title { text-align: center; font-weight: bold; font-size: 14px; margin: 4px 0; }
            .receipt-invoice { text-align: center; font-size: 11px; margin: 2px 0; }
            .receipt-customer { text-align: center; font-size: 11px; margin: 2px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; margin: 6px 0; }
            th, td { padding: 2px; border-bottom: 1px dotted #ddd; }
            th { text-align: left; border-bottom: 2px solid #000; }
            .text-right { text-align: right; }
            .summary-line { display: flex; justify-content: space-between; padding: 2px 0; }
            .summary-line .value { font-weight: bold; }
            .discount-breakdown { padding-left: 12px; font-size: 9px; color: #555; }
            .total-line { border-top: 2px solid #000; font-weight: bold; font-size: 13px; padding-top: 4px; margin-top: 4px; }
            .footer { text-align: center; margin-top: 12px; border-top: 1px dashed #000; padding-top: 8px; font-size: 9px; }
            .batch-col { font-size: 9px; color: #555; }
            img { max-width: 100%; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);

    win.document.close();
    waitForImagesThenPrint(win, () => {
      win.focus();
      win.print();
      win.close();
    });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const primaryColor = '#2e3192';
  const secondaryColor = '#f1592a';

  const totalTax = filteredData.reduce((sum, item) => sum + (item.is_return ? -1 : 1) * (item.tax_amount || 0), 0);
  const totalDiscount = filteredData.reduce((sum, item) => sum + (item.is_return ? -1 : 1) * ((item.discount_amount || 0) + (item.manual_discount || 0)), 0);
  const totalShippingOther = filteredData.reduce((sum, item) => sum + (item.is_return ? -1 : 1) * ((item.shipping_cost || 0) + (item.other_charges || 0)), 0);
  
  // ✅ Calculate Gross Profit accurately
  const grossProfit = filteredData.reduce((sum, item) => {
    const sign = item.is_return ? -1 : 1;
    const unitCost = item.inventory_unit_cost || 0;
    const grossLine = (item.unit_price || 0) * (item.quantity || 0);
    const lineDiscount = (item.discount_amount || 0) + (item.manual_discount || 0);
    const lineIncome = sign * (grossLine - lineDiscount - (unitCost * item.quantity));
    return sum + lineIncome;
  }, 0);

  const netProfit = grossProfit - totalExpenses;

  const invoiceGrandTotalMap = {};
  filteredData.forEach(item => {
    const invId = item.sales_invoice_id?._id || item.sales_invoice_id;
    if (invId && !invoiceGrandTotalMap[invId]) {
      invoiceGrandTotalMap[invId] = item.invoice_grand_total || 0;
    }
  });
  const grandTotal = Object.values(invoiceGrandTotalMap).reduce((sum, val) => sum + val, 0);

  const productOptions = products.map(p => ({ value: p._id, label: `${p.product_code} - ${p.product_name}` }));
  const customerOptions = customers.map(c => ({ value: c._id, label: c.customer_name }));
  const batchOptions = batchNumbers.map(b => ({ value: b, label: b }));

  if (loadingCompany) {
    return <div style={{ color: secondaryColor, textAlign: 'center', marginTop: '2rem' }}>Loading company data...</div>;
  }

  const logoUrl = getLogoUrl();
  const hasRecords = filteredData.length > 0;

  // ✅ Extract address, phones, email
  const companyAddress = company?.admin_user?.Usercomp_address || company?.address || '';
  const companyContact = company?.admin_user?.Usercomp_contact || company?.contact || '';
  const companyContact2 = company?.admin_user?.Usercomp_contact_2 || company?.contact_2 || '';
  const companyContact3 = company?.admin_user?.Usercomp_contact_3 || company?.contact_3 || '';
  const companyEmail = company?.admin_user?.Usercomp_email || company?.admin_email || '';
  const phoneLine = [companyContact, companyContact2, companyContact3].filter(Boolean).join(' · ');

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const companyCurrency = getCompanyCurrency(company);

  const rangeLabel = (() => {
    if (fromDate && toDate) return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
    if (fromDate) return `From ${new Date(fromDate).toLocaleDateString()}`;
    if (toDate) return `Until ${new Date(toDate).toLocaleDateString()}`;
    return 'All Time';
  })();

  const firstItem = filteredData.length > 0 ? filteredData[0] : null;
  const customerName = firstItem?.customer_id?.customer_name || firstItem?.customer_name || '';
  const paymentMethod = firstItem?.payment_method && firstItem.payment_method.length > 0
    ? firstItem.payment_method[0]
    : '—';
  const cashierName = firstItem?.created_by?.Usercomp_sure_name || firstItem?.created_by?.name || 'System';

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '1.5rem' }}>
      {/* ============================================================
          SCREEN PREVIEW
      ============================================================ */}
      <div className="screen-only" ref={printRef}>
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem', borderLeft: `4px solid ${primaryColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {logoUrl && (
                <img src={logoUrl} alt="Logo" style={{ height: '64px', width: '64px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #ddd' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: primaryColor }}>{company?.company_name || 'Company Name'}</h1>
                <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>{company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#495057', fontSize: '0.875rem' }}>
              <p>{companyAddress || 'No address'}</p>
              <p style={{ marginTop: '0.25rem' }}>
                📞 {companyContact || 'No phone'}
                {companyContact2 ? ` · ${companyContact2}` : ''}
                {companyContact3 ? ` · ${companyContact3}` : ''}
                <br />
                ✉️ {companyEmail || 'No email'}
              </p>
              <p>Fiscal year start: {company?.fiscal_year_start ? new Date(company.fiscal_year_start).toLocaleDateString() : 'Not set'}</p>
            </div>
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #e9ecef', paddingTop: '0.5rem' }}>
            <h2 style={{ textAlign: 'center', color: secondaryColor }}>Sales Report</h2>
          </div>
        </div>

        {/* FILTERS (no-print) */}
        <div className="no-print" style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <SearchableDropdown options={productOptions} value={selectedProductId} onChange={setSelectedProductId} placeholder="Search product..." label="Product" />
              {loadingProducts && <small style={{ display: 'block', marginTop: '4px' }}>Loading products...</small>}
              {!loadingProducts && products.length === 0 && <small style={{ display: 'block', marginTop: '4px', color: 'red' }}>No products found. Check backend.</small>}
            </div>
            <div>
              <SearchableDropdown options={customerOptions} value={selectedCustomerId} onChange={setSelectedCustomerId} placeholder="Search customer..." label="Customer" />
              {loadingCustomers && <small style={{ display: 'block', marginTop: '4px' }}>Loading customers...</small>}
            </div>
            <div>
              <SearchableDropdown options={batchOptions} value={selectedBatchNo} onChange={setSelectedBatchNo} placeholder="Search batch..." label="Batch No" />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>Invoice No</label>
              <input type="text" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="Search by invoice number" style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleGenerate} disabled={loading} style={{ flex: 1, backgroundColor: primaryColor, color: 'white', padding: '0.5rem', borderRadius: '0.375rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>{loading ? 'Loading...' : 'Generate Report'}</button>
              <button onClick={handlePrint} disabled={filteredData.length === 0} style={{ backgroundColor: secondaryColor, color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredData.length === 0 ? 0.6 : 1 }}>🖨️ Print</button>
              <button onClick={handlePosPrint} disabled={filteredData.length === 0} style={{ backgroundColor: '#28a745', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredData.length === 0 ? 0.6 : 1 }}>🧾 Print POS</button>
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.75rem', fontStyle: 'italic' }}>
            * Date range filters transaction dates. Includes sales and returns. Batch No and Invoice No filter client‑side.
          </div>
        </div>

        {/* Summary cards */}
        <div className="summary-grid no-print">
          <div className="summary-card debit-summary">
            <div className="summary-icon">TX</div>
            <div>
              <span className="summary-label">Total Tax</span>
              <h3>{formatCurrency(totalTax)}</h3>
            </div>
          </div>
          <div className="summary-card credit-summary">
            <div className="summary-icon">DC</div>
            <div>
              <span className="summary-label">Total Discount</span>
              <h3>{formatCurrency(totalDiscount)}</h3>
            </div>
          </div>
          <div className="summary-card accounts-summary">
            <div className="summary-icon">SH</div>
            <div>
              <span className="summary-label">Shipping + Other</span>
              <h3>{formatCurrency(totalShippingOther)}</h3>
            </div>
          </div>
          <div className={`summary-card ${hasRecords ? 'balance-summary' : 'difference-summary'}`}>
            <div className="summary-icon">{hasRecords ? '✓' : '!'}</div>
            <div>
              <span className="summary-label">{hasRecords ? 'Grand Total' : 'No Records'}</span>
              <h3>{hasRecords ? formatCurrency(grandTotal) : '—'}</h3>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
          {error && <div style={{ padding: '1rem', color: 'red', textAlign: 'center' }}>{error}</div>}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: primaryColor, color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem' }}>No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Invoice No</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Customer</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Item Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Batch No</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Unit Price</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tax</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Discount</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Shipping + Other</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Income</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && !loading && (
                    <tr><td colSpan="13" style={{ textAlign: 'center', padding: '2rem' }}>No sales records found. Click Generate Report.</td></tr>
                  )}
                  {filteredData.map((item, idx) => {
                    const product = item.product_id || {};
                    const customer = item.customer_id || {};
                    const sign = item.is_return ? -1 : 1;
                    const tax = sign * (item.tax_amount || 0);
                    const combinedDiscount = sign * ((item.discount_amount || 0) + (item.manual_discount || 0));
                    const shippingOther = sign * ((item.shipping_cost || 0) + (item.other_charges || 0));
                    const totalAmount = item.invoice_grand_total || item.grand_total || 0;
                    
                    // ✅ Calculate Line Income accurately
                    const unitCost = item.inventory_unit_cost || 0;
                    const grossLine = (item.unit_price || 0) * (item.quantity || 0);
                    const lineDiscount = (item.discount_amount || 0) + (item.manual_discount || 0);
                    const lineIncome = sign * (grossLine - lineDiscount - (unitCost * (item.quantity || 0)));

                    return (
                      <tr key={item._id} style={{ borderBottom: '1px solid #f1f3f5', backgroundColor: item.is_return ? '#fff0f0' : 'white' }}>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ padding: '0.75rem' }}>{item.invoice_no_display || item.invoice_no || item.reference_id || '—'} {item.is_return && <span style={{color: 'red', fontSize: '10px'}}>(Return)</span>}</td>
                        <td style={{ padding: '0.75rem' }}>{formatDate(item.transaction_date)}</td>
                        <td style={{ padding: '0.75rem' }}>{customer.customer_name || 'Unknown'}</td>
                        <td style={{ padding: '0.75rem' }}>{product.product_name || 'Unknown'}</td>
                        <td style={{ padding: '0.75rem' }}>{item.batch_number || '—'}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{sign * item.quantity}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(tax)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(combinedDiscount)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(shippingOther)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: lineIncome >= 0 ? '#15803d' : '#b91c1c' }}>
                          {formatCurrency(lineIncome)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(totalAmount)}</td>
                      </tr>
                    );
                  })}
                  {filteredData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="7" style={{ padding: '0.75rem', textAlign: 'right' }}>Grand Totals:</td>
                      <td></td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalTax)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalDiscount)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalShippingOther)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: grossProfit >= 0 ? '#15803d' : '#b91c1c' }}>
                        {formatCurrency(grossProfit)}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(grandTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="hide-on-print" style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem', color: '#adb5bd' }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>

      {/* ============================================================
          PRINT-ONLY A4 LANDSCAPE REPORT
      ============================================================ */}
      <div className="print-only print-page">
        <div className="print-header">
          <div className="print-header-left">
            {logoUrl && (
              <img src={logoUrl} alt="Logo" className="print-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">{company?.company_name || 'Company Name'}</h1>
              <p className="print-company-line">{companyAddress || 'No address'}</p>
              {phoneLine && <p className="print-company-line">Tel: {phoneLine}</p>}
              <p className="print-company-line">
                {companyEmail ? `Email: ${companyEmail}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">{fiscalYearLabel}-{fiscalYearLabel + 1}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        <div className="print-title-block">
          <h2 className="print-report-title">SALES REPORT</h2>
          <p className="print-report-subtitle">{rangeLabel} · {filteredData.length} record(s)</p>
        </div>

        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Item Name</th>
              <th>Batch</th>
              <th className="print-col-qty">Qty</th>
              <th className="print-col-money">Unit Price</th>
              <th className="print-col-money">Tax</th>
              <th className="print-col-money">Discount</th>
              <th className="print-col-money">Shipping+Other</th>
              <th className="print-col-money">Income</th>
              <th className="print-col-money">Total</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="13" className="print-empty">No sales records found for the selected filters.</td>
              </tr>
            ) : (
              filteredData.map((item, idx) => {
                const product = item.product_id || {};
                const customer = item.customer_id || {};
                const sign = item.is_return ? -1 : 1;
                const tax = sign * (item.tax_amount || 0);
                const combinedDiscount = sign * ((item.discount_amount || 0) + (item.manual_discount || 0));
                const shippingOther = sign * ((item.shipping_cost || 0) + (item.other_charges || 0));
                const totalAmount = item.invoice_grand_total || item.grand_total || 0;
                
                // ✅ Calculate Line Income accurately
                const unitCost = item.inventory_unit_cost || 0;
                const grossLine = (item.unit_price || 0) * (item.quantity || 0);
                const lineDiscount = (item.discount_amount || 0) + (item.manual_discount || 0);
                const lineIncome = sign * (grossLine - lineDiscount - (unitCost * (item.quantity || 0)));

                return (
                  <tr key={item._id || `p-${idx}`}>
                    <td className="print-col-no">{idx + 1}</td>
                    <td>{item.invoice_no_display || item.invoice_no || item.reference_id || '—'} {item.is_return && '(R)'}</td>
                    <td>{formatDate(item.transaction_date)}</td>
                    <td>{customer.customer_name || 'Unknown'}</td>
                    <td>{product.product_name || 'Unknown'}</td>
                    <td>{item.batch_number || '—'}</td>
                    <td className="print-col-qty">{sign * item.quantity}</td>
                    <td className="print-col-money">{formatCurrency(item.unit_price)}</td>
                    <td className="print-col-money">{formatCurrency(tax)}</td>
                    <td className="print-col-money">{formatCurrency(combinedDiscount)}</td>
                    <td className="print-col-money">{formatCurrency(shippingOther)}</td>
                    <td className="print-col-money" style={{ color: lineIncome >= 0 ? '#15803d' : '#b91c1c' }}>{formatCurrency(lineIncome)}</td>
                    <td className="print-col-money print-total-value">{formatCurrency(totalAmount)}</td>
                  </tr>
                );
              })
            )}
            {filteredData.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="8" className="print-total-label">GRAND TOTAL</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalTax)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalDiscount)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalShippingOther)}</td>
                <td className="print-col-money print-total-value" style={{ color: grossProfit >= 0 ? '#15803d' : '#b91c1c' }}>{formatCurrency(grossProfit)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(grandTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Net Profit Card */}
        <div className="print-net-card" style={{ marginTop: '8mm', border: '1pt solid #170f48', borderRadius: '3mm', textAlign: 'center', padding: '5mm' }}>
          <div style={{ fontSize: '9.5pt', textTransform: 'uppercase', fontWeight: 700, color: '#170f48', marginBottom: '2mm' }}>
            Net Profit (Gross Profit - Expenses)
          </div>
          <div style={{ fontSize: '18pt', fontWeight: 800, color: netProfit >= 0 ? '#15803d' : '#b91c1c' }}>
            {formatCurrency(netProfit)}
          </div>
          <div style={{ fontSize: '8pt', color: '#666', marginTop: '2mm' }}>
            Gross Profit: {formatCurrency(grossProfit)} | Expenses: {formatCurrency(totalExpenses)}
          </div>
        </div>

        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* ============================================================
          POS RECEIPT — hidden offscreen, used by handlePosPrint
      ============================================================ */}
      <div style={{ display: 'none' }}>
        <div ref={posPrintRef}>
          <div className="receipt-header">
            {logoUrl && <img src={logoUrl} alt="Logo" className="logo" />}
            <h1>{company?.company_name || 'Company Name'}</h1>
            <p>{companyAddress || ''}</p>
            <p>
              {phoneLine ? `📞 ${phoneLine}` : ''}
              {phoneLine && companyEmail ? ' | ' : ''}
              {companyEmail ? `✉️ ${companyEmail}` : ''}
            </p>
            <p>Fiscal Year: {fiscalYearLabel}</p>
          </div>
          <div className="receipt-title">SALES RECEIPT</div>
          {firstItem && (
            <>
              <div className="receipt-invoice">
                Invoice No: {firstItem.invoice_no_display || firstItem.invoice_no || firstItem.reference_id || ''}
              </div>
              <div className="receipt-customer">
                Customer: {customerName}
              </div>
            </>
          )}
          <p style={{ textAlign: 'center', fontSize: '9px', margin: '2px 0 6px' }}>
            Date: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
          </p>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Batch</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, idx) => {
                const product = item.product_id || {};
                const sign = item.is_return ? -1 : 1;
                return (
                  <tr key={item._id}>
                    <td>{idx + 1}</td>
                    <td>{product.product_name || 'Unknown'} {item.is_return && '(R)'}</td>
                    <td className="batch-col">{item.batch_number || '—'}</td>
                    <td className="text-right">{sign * item.quantity}</td>
                    <td className="text-right">{formatCurrency(item.unit_price)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {(() => {
            let subtotal = 0;
            let totalDiscountPOS = 0;
            let totalTaxPOS = 0;
            let totalShipping = 0;
            let totalOther = 0;
            filteredData.forEach(item => {
              const sign = item.is_return ? -1 : 1;
              const qty = sign * (item.quantity || 0);
              const price = item.unit_price || 0;
              subtotal += qty * price;
              totalDiscountPOS += sign * ((item.discount_amount || 0) + (item.manual_discount || 0));
              totalTaxPOS += sign * (item.tax_amount || 0);
              totalShipping += sign * (item.shipping_cost || 0);
              totalOther += sign * (item.other_charges || 0);
            });

            let discountPercentAmount = 0;
            let manualDiscountAmount = 0;
            filteredData.forEach(item => {
              const sign = item.is_return ? -1 : 1;
              discountPercentAmount += sign * (item.discount_amount || 0);
              manualDiscountAmount += sign * (item.manual_discount || 0);
            });

            return (
              <>
                <div className="summary-line">
                  <span>Subtotal</span>
                  <span className="value">{formatCurrency(subtotal)}</span>
                </div>
                <div className="summary-line">
                  <span>Discount</span>
                  <span className="value" style={{ color: 'red' }}>-{formatCurrency(totalDiscountPOS)}</span>
                </div>
                {discountPercentAmount !== 0 && (
                  <div className="discount-breakdown">
                    <div className="summary-line" style={{ fontSize: '9px' }}>
                      <span>Percentage Discount</span>
                      <span>-{formatCurrency(discountPercentAmount)}</span>
                    </div>
                  </div>
                )}
                {manualDiscountAmount !== 0 && (
                  <div className="discount-breakdown">
                    <div className="summary-line" style={{ fontSize: '9px' }}>
                      <span>Manual Discount</span>
                      <span>-{formatCurrency(manualDiscountAmount)}</span>
                    </div>
                  </div>
                )}
                {totalTaxPOS !== 0 && (
                  <div className="summary-line">
                    <span>Tax</span>
                    <span className="value">+{formatCurrency(totalTaxPOS)}</span>
                  </div>
                )}
                {totalShipping !== 0 && (
                  <div className="summary-line">
                    <span>Shipping</span>
                    <span className="value">+{formatCurrency(totalShipping)}</span>
                  </div>
                )}
                {totalOther !== 0 && (
                  <div className="summary-line">
                    <span>Other Charges</span>
                    <span className="value">+{formatCurrency(totalOther)}</span>
                  </div>
                )}
                <div className="summary-line total-line">
                  <span>TOTAL</span>
                  <span className="value">{formatCurrency(grandTotal)}</span>
                </div>
                <div className="summary-line">
                  <span>Paid</span>
                  <span className="value">{formatCurrency(grandTotal)}</span>
                </div>
                <div className="summary-line">
                  <span>Balance</span>
                  <span className="value">{formatCurrency(0)}</span>
                </div>
                <div className="summary-line">
                  <span>Payment Method</span>
                  <span className="value">{paymentMethod}</span>
                </div>
                <div className="summary-line">
                  <span>Served By</span>
                  <span className="value">{cashierName}</span>
                </div>
              </>
            );
          })()}

          <div className="footer">
            <p>Thank you for your business!</p>
            <p>Generated on {new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* ============================================================
          INLINE PRINT STYLES
      ============================================================ */}
      <style>{`
        .print-only { display: none; }

        @page {
          size: A4 landscape;
          margin: 10mm 12mm 10mm 12mm;
        }

        @media print {
          .no-print { display: none !important; }
          .hide-on-print { display: none !important; }
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          /* Hide EVERYTHING (sidebar, menu, filters, POS block) */
          body * { visibility: hidden !important; }

          /* Show only the print block */
          .print-only,
          .print-only * { visibility: visible !important; }

          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }

          .screen-only,
          .no-print,
          .hide-on-print { display: none !important; }

          /* ---------- PRINT BLOCK LAYOUT ---------- */
          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 3mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 4mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 16mm; max-width: 24mm; object-fit: contain; }
          .print-company-name { font-size: 13pt; font-weight: 700; color: #170f48; margin: 0 0 0.8mm 0; }
          .print-company-line { font-size: 8pt; color: #444; margin: 0.3mm 0; }
          .print-header-right { text-align: right; font-size: 8pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 0.8mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 24mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 4mm; }
          .print-report-title { font-size: 13pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 0.8mm 0; }
          .print-report-subtitle { font-size: 9pt; color: #555; margin: 0; }

          /* ---------- PRINT TABLE ---------- */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
            page-break-inside: auto;
          }
          .print-table thead { display: table-header-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999;
            padding: 2.5pt 3.5pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 7.5pt;
            text-transform: uppercase;
            letter-spacing: 0.2pt;
          }
          .print-col-no    { width: 8mm; text-align: center; }
          .print-col-qty   { width: 14mm; text-align: right; }
          .print-col-money {
            width: 20mm;
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }

          .print-total-row { background: #f4f4f4 !important; font-weight: 700; }
          .print-total-label { text-align: right; letter-spacing: 0.5pt; }
          .print-total-value { font-weight: 700; }
          .print-empty { text-align: center; padding: 12pt; font-style: italic; }

          /* ---------- PRINT FOOTER ---------- */
          .print-footer { margin-top: 8mm; page-break-inside: avoid; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 4mm;
          }
          .print-signature { flex: 1; text-align: center; font-size: 8pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default Salesreport;
